"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { FaBars } from "react-icons/fa"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { Gamepad2, Bell, Settings, LogOut, Users, Trophy, MessageCircle, BarChart2 } from "lucide-react"
import { useUnreadNotifications } from "../hooks/useUnreadNotifications"

const COMMUNITY_ALLOWED_EMAIL = "paulter17@gmail.com"

function Header({ onToggle, currentView, onShowNotifications }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const { currentUser, userProfile, logout, systemSettings } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const unreadCount = useUnreadNotifications()
  const canSeeCommunity =
    !systemSettings?.hideCommunity ||
    (currentUser?.email || "").toLowerCase() === COMMUNITY_ALLOWED_EMAIL
  const canSeeScores =
    !systemSettings?.hideScores ||
    (currentUser?.email || "").toLowerCase() === COMMUNITY_ALLOWED_EMAIL

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768) // 768px is typical md breakpoint
    }

    handleResize() // Initial check
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const toggleMenu = () => {
    setMenuOpen(!menuOpen)
  }
  const closeMenu = () => {
    setMenuOpen(false)
  }

  const noHeaderPaths = ["/home", "/signin", "/signup"]

  if (noHeaderPaths.includes(location.pathname.toLowerCase())) return null

  return (
    <div className="relative min-h-[8vh] text-white flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900/90 backdrop-blur-md shadow-lg">
      <div className="flex items-center space-x-4">
        <button
          className="md:hidden bg-gray-800/70 p-2 rounded-lg hover:bg-gray-700/70 transition-colors"
          onClick={toggleMenu}
        >
          <FaBars size={20} className="text-emerald-400" />
        </button>
        <div className="hidden md:flex space-x-6">
          {currentView !== "notifications" && currentView !== "settings" ? (
            <>
              <Link
                to="#"
                onClick={() => {
                  onToggle("home")
                  closeMenu()
                }}
                className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium"
              >
                Home
              </Link>
              <Link
                to="#"
                onClick={() => {
                  onToggle("allBets")
                  closeMenu()
                }}
                className="text-gray-300 hover:text-white transition-colors"
              >
                All Bets
              </Link>
              <Link
                to="#"
                onClick={() => {
                  onToggle("myBets")
                  closeMenu()
                }}
                className="text-gray-300 hover:text-white transition-colors"
              >
                My Bets
              </Link>
              {canSeeCommunity && (
                <Link
                  to="/Community"
                  onClick={() => closeMenu()}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Community
                </Link>
              )}
              {canSeeScores && (
                <Link
                  to="/Scores"
                  onClick={() => closeMenu()}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Scores
                </Link>
              )}
              <Link
                 to="/FriendsBets"
                 onClick={() => {
                   onToggle("friendsBets")
                   closeMenu()
                 }}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Friends
              </Link>
              <Link
                to="#"
                onClick={() => {
                  onShowNotifications(true)
                  closeMenu()
                }}
                className="text-gray-300 hover:text-white transition-colors relative"
              >
                Notifications
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-6 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Link>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center">
                <h1 className="text-lg md:text-xl">
                  <button onClick={() => navigate("/")}>
                    <span className="font-bold text-white">Rivals</span>
                    <span className="font-bold text-emerald-400"> Sportsbook</span>
                  </button>
                </h1>
              </div>
            </>
          )}
        </div>
      </div>

      {currentView !== "notifications" && currentView !== "settings" ? (
        <div className="flex items-center justify-center md:ml-[-70px]">
          <h1 className="text-lg md:text-xl">
            <button onClick={() => navigate("/")}>
              <div className="flex items-center space-x-2">
                <span className="font-bold text-white">
                  Rivals <span className="text-emerald-400">Sportsbook</span>
                </span>
              </div>
            </button>
          </h1>
        </div>
      ) : null}

      <div className="flex items-center space-x-4">
        <Link to="/Settings?tab=deposit" className="hover:text-gray-400">
          {userProfile && (
            <div className="flex items-center space-x-2 text-right bg-gray-800/70 px-3 py-1.5 rounded-lg">
              {!isMobile && <span className="text-sm text-gray-300">{userProfile?.full_name}</span>}
              <span className="text-sm font-medium text-emerald-400">${(userProfile?.account_balance || 0).toFixed(2)}</span>
            </div>
          )}
        </Link>
        <Link to="/Settings" className="hover:text-gray-400">
          {userProfile && (
            <div className="flex items-center space-x-2">
              <img
                src={userProfile.profile_picture || "/placeholder.svg"}
                alt="Profile"
                className="w-10 h-10 rounded-full border-2 border-gray-700 hover:border-emerald-500 transition-colors"
              />
            </div>
          )}
        </Link>
      </div>

      {menuOpen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm" style={{ position: 'fixed', zIndex: 9999 }}>
          <div className="absolute top-16 left-4 right-4 bg-gray-900/90 backdrop-blur-md border border-gray-800 rounded-xl p-4 shadow-xl" style={{ position: 'absolute', zIndex: 10000 }}>
            <div className="flex flex-col space-y-4">
              {currentView !== "notifications" && currentView !== "settings" ? (
                <>
                  <Link
                    to="#"
                    onClick={() => {
                      onToggle("home")
                      closeMenu()
                    }}
                    className="flex items-center space-x-3 text-emerald-400 hover:text-emerald-300 transition-colors py-2"
                  >
                    <Gamepad2 className="w-5 h-5" />
                    <span>Home</span>
                  </Link>
                                  <Link
                  to="#"
                  onClick={() => {
                    onToggle("allBets")
                    closeMenu()
                  }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                >
                  <Users className="w-5 h-5" />
                  <span>All Bets</span>
                </Link>
                <Link
                  to="#"
                  onClick={() => {
                    onToggle("myBets")
                    closeMenu()
                  }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                >
                  <Trophy className="w-5 h-5" />
                  <span>My Bets</span>
                </Link>
                  {canSeeCommunity && (
                    <Link
                      to="/Community"
                      onClick={() => closeMenu()}
                      className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>Community</span>
                    </Link>
                  )}
                  {canSeeScores && (
                    <Link
                      to="/Scores"
                      onClick={() => closeMenu()}
                      className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                    >
                      <BarChart2 className="w-5 h-5" />
                      <span>Scores</span>
                    </Link>
                  )}
                <Link
                  to="/FriendsBets"
                  onClick={() => {
                    onToggle("friendsBets")
                    closeMenu()
                  }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                >
                  <Users className="w-5 h-5" />
                  <span>Friends</span>
                </Link>
                  <Link
                    to="#"
                    onClick={() => {
                      onShowNotifications(true)
                      closeMenu()
                    }}
                    className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2 relative"
                  >
                    <Bell className="w-5 h-5" />
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold ml-2">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/Settings"
                    className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                  >
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                  </Link>
                  <div className="border-t border-gray-700 my-2"></div>
                  {currentUser && (
                    <button
                      onClick={logout}
                      className="flex items-center space-x-3 text-red-400 hover:text-red-300 transition-colors py-2"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={() => {
                      onToggle("home")
                      closeMenu()
                    }}
                    className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                  >
                    <Gamepad2 className="w-5 h-5" />
                    <span>Home</span>
                  </Link>
                  {canSeeCommunity && (
                    <Link
                      to="/Community"
                      onClick={() => closeMenu()}
                      className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>Community</span>
                    </Link>
                  )}
                  {canSeeScores && (
                    <Link
                      to="/Scores"
                      onClick={() => closeMenu()}
                      className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors py-2"
                    >
                      <BarChart2 className="w-5 h-5" />
                      <span>Scores</span>
                    </Link>
                  )}
                  <div className="border-t border-gray-700 my-2"></div>
                  {currentUser && (
                    <button
                      onClick={logout}
                      className="flex items-center space-x-5 text-red-400 hover:text-red-300 transition-colors py-2"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Header

